/**
 * The floor the treatment verbs stand on (M4-01…05).
 *
 * Six small things that every verb on the medicine ladder does the same way:
 * grow the treater's familiarity, notice whether somebody wiser is at their
 * elbow, notice whether they are working blind, and say the three sentences —
 * *nothing to be done*, *the hand slipped*, *whose wound this is* — that a
 * player learns the rules from.
 *
 * They lived in `interact.ts` until M4-05 wanted them too, and the choice at
 * that point was between a cycle, a copy, and a module. A cycle is what
 * `depcruise` exists to refuse; a copy would have meant two places that decide
 * what a fumble reads as, drifting apart the first time somebody rewrites one
 * of them. So: a module, and deliberately the smallest one in the package.
 *
 * Not in `medicine.ts`, which is the arithmetic and says at the top of itself
 * that it holds no world state. These do — they read the light on a tile and
 * write a character's lore — and the distinction between "what a treatment is
 * worth" and "what the world does about it" is worth one more file.
 */
import { chebyshev } from './actor.js';
import { createLore, growMedicine } from './item.js';
import { treaterInDark } from './medicine.js';
/**
 * Grow the treater's medicine familiarity by one genuine treatment — two if
 * somebody who knows more is at their elbow (the teaching-transfer stub). Only
 * ever called when the treatment actually did something, so a fumble teaches
 * nothing and a no-op is not even reached.
 */
export function learnMedicine(ctx, actor) {
    const lore = (ctx.world.lore[actor.id] ??= createLore());
    growMedicine(lore, mentorNear(ctx, actor, lore.medicine ?? 0));
}
/** Is a more-practised character standing over the treater's shoulder? */
export function mentorNear(ctx, actor, myMedicine) {
    for (const id of Object.keys(ctx.world.lore)) {
        if (id === actor.id)
            continue;
        const other = ctx.world.actors[id];
        if (!other || chebyshev(actor.x, actor.y, other.x, other.y) > 1)
            continue;
        if ((ctx.world.lore[id]?.medicine ?? 0) > myMedicine)
            return true;
    }
    return false;
}
/** Whether the treater is working blind, for the darkness die. */
export function workingBlind(ctx, actor) {
    return treaterInDark(ctx.world.lights, ctx.time, actor.x, actor.y, ctx.tick);
}
/** The one-line "nothing to do" every finisher shares. */
export function nothingToTreat(ctx, actor, target, verb) {
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, target.id],
        refs: [verb, 'nothing-to-do'],
        text: `${actor.name} finds nothing to be done.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/** The cruel line every fumble shares, per M4-01's "narrated cruelly". */
export function fumbleLine(ctx, actor, target, verb, wound) {
    ctx.emit({
        type: 'interacted',
        actors: [actor.id, target.id],
        refs: [verb, 'fumble', wound.id, wound.part],
        text: target.id === actor.id
            ? `${actor.name}'s hands slip in the blood, and the wound gives worse.`
            : `${actor.name}'s hands slip; ${target.name}'s ${wound.zone} gives worse.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/** `you`/`their` grammar for the patient's wound — self reads differently. */
export function patientPossessive(actor, target) {
    return target.id === actor.id ? 'their own' : `${target.name}'s`;
}
//# sourceMappingURL=care.js.map